/**
 * Bringing an instance nobody created here under management.
 *
 * A scan finds instances; this is what turns one into something the control
 * plane can operate. The missing facts are the ones a scan cannot see —
 * chiefly **where its Terraform state lives**, which is in the customer's own
 * bucket under a key the installer chose, and is not derivable from anything
 * AWS will tell us.
 *
 * Those facts are all in the `config.json` the instance was deployed with, so
 * the customer uploads that file rather than transcribing five values.
 *
 * ## The upload never leaves the browser
 *
 * `config.json` carries `account_config.access_key_id`,
 * `secret_access_key` and `session_token` — **live AWS credentials for the
 * customer's account** — and `variables.registry_key` and whatever other
 * secrets the chart takes.
 *
 * The control plane's central promise is that it never holds customer AWS
 * credentials. So the file is read in the browser, this function takes the five
 * fields that matter, and **only those are sent**. The credentials never cross
 * the network, never reach a request log, and never enter the database. A
 * server-side strip would be simpler and would break the property: by the time
 * anything stripped them they would already be in Qrvey's account.
 *
 * That is why this lives in `shared` rather than `api`. It is the browser that
 * has to be trusted to drop them, so the browser is where the dropping is
 * written, tested, and readable.
 *
 * ## It refuses rather than guesses
 *
 * A half-read config produces an adoption pointing at the wrong state file, and
 * a run against that either finds nothing and builds a second instance or
 * writes over a state file belonging to something else. Both are worse than
 * telling someone the file is not what they thought it was.
 */
import { splitSecretVariables } from '../deployment/secrets.js';
export const isAdoptionProblem = (read) => 'problem' in read;
const text = (value) => typeof value === 'string' && value.trim() !== '' ? value.trim() : undefined;
/**
 * Reads an uploaded `config.json`.
 *
 * Takes the parsed document rather than a string so the caller owns the JSON
 * error — "that file is not JSON" and "that JSON is not a Qrvey config" are
 * different things to tell somebody.
 */
export const readAdoptionConfig = (document) => {
    if (typeof document !== 'object' || document === null || Array.isArray(document)) {
        return { problem: 'That file does not look like a Qrvey config.json.' };
    }
    const root = document;
    const account = (root['account_config'] ?? {});
    const variables = (root['variables'] ?? {});
    const deploymentId = text(variables['deployment_id']);
    const region = text(account['region']);
    const stateBucket = text(account['bucket']);
    const stateKey = text(account['key']);
    /*
     * Named individually rather than "the file is invalid".
     *
     * These uploads go wrong in a predictable way — the wrong config, or one
     * edited by hand — and the person holding the file can fix it in a second if
     * told which field is missing, and not at all if told the file is bad.
     */
    const missing = [
        deploymentId === undefined ? 'variables.deployment_id' : undefined,
        region === undefined ? 'account_config.region' : undefined,
        stateBucket === undefined ? 'account_config.bucket' : undefined,
        stateKey === undefined ? 'account_config.key' : undefined,
    ].filter((name) => name !== undefined);
    /*
     * One check that narrows all four, rather than a length test followed by a
     * second guard.
     *
     * It was written the other way round first — `missing.length > 0`, then a
     * separate `stateBucket === undefined || stateKey === undefined` to satisfy
     * the narrowing. That second block was **unreachable**: both fields are
     * already in `missing`, so the length test always fired first, and its
     * message could never be produced. Naming the four conditions directly makes
     * the type checker's requirement and the runtime behaviour the same thing.
     */
    if (deploymentId === undefined ||
        region === undefined ||
        stateBucket === undefined ||
        stateKey === undefined) {
        return {
            problem: `That config.json is missing ${missing.join(', ')}. It should be the file the instance was deployed with.`,
        };
    }
    const chartVersion = text(variables['qrvey_chart_version']);
    /*
     * Every variable the file carried, minus everything secret-shaped.
     *
     * `account_config` is not in here at all, which is where the live AWS
     * credentials live — this reads `variables` only, so those are excluded by
     * construction rather than by a rule that could stop matching.
     */
    const { redacted } = splitSecretVariables(variables);
    return {
        deploymentId,
        region,
        stateBucket,
        stateKey,
        ...(chartVersion === undefined ? {} : { chartVersion }),
        ...(Object.keys(redacted).length === 0 ? {} : { variables: redacted }),
    };
};
/**
 * The fields in a config that must never be sent, for the test that proves it.
 *
 * Kept as data rather than left implicit in `readAdoptionConfig`'s shape,
 * because the property worth pinning is not "this function returns five fields"
 * — it is that a document containing these produces an object containing none
 * of them, however the extraction is later rewritten.
 */
export const CONFIG_SECRET_PATHS = [
    ['account_config', 'access_key_id'],
    ['account_config', 'secret_access_key'],
    ['account_config', 'session_token'],
    ['variables', 'registry_key'],
    ['variables', 'openai_api_key'],
    ['variables', 'globalization', 'google_client_private_key'],
];
//# sourceMappingURL=adoption.js.map